<script setup lang="ts">
interface Props {
  width?: string;
}

const props = withDefaults(defineProps<Props>(), {
  width: "100%",
});

const emit = defineEmits<{
  addressChanged: [address: { formatted: Record<string, string>; raw: Record<string, unknown> }];
}>();

const config = useRuntimeConfig();
const searchTextField = ref<HTMLInputElement | null>(null);
const addressString = ref("");
const addresses = ref<Record<string, unknown>[]>([]);
const locationLoading = ref(false);
let queryTimer: ReturnType<typeof setTimeout> | null = null;

function clearInput() {
  if (searchTextField.value) searchTextField.value.value = "";
  addressString.value = "";
}

function displayAddress(address: Record<string, unknown>): string {
  return `${address.Text || ""} ${(address.Description as string)?.trim() || ""}`.trim();
}

function locatorButtonPressed() {
  if (!navigator.geolocation) {
    const { $notify } = useNuxtApp();
    $notify({
      text: "Your browser doesn't support location lookup. Type your address instead.",
      variant: "warning",
    });
    return;
  }
  locationLoading.value = true;
  navigator.geolocation.getCurrentPosition(
    (pos) => {
      fetchGeoAddresses(pos.coords.latitude, pos.coords.longitude);
    },
    (err) => {
      locationLoading.value = false;
      console.error("[LocationBox] geolocation denied/failed", err);
      const { $notify } = useNuxtApp();
      const text =
        err.code === err.PERMISSION_DENIED
          ? "Location permission denied. Type your address instead."
          : "Couldn't get your location. Type your address instead.";
      $notify({ text, variant: "warning" });
    },
  );
}

async function fetchGeoAddresses(lat: number, long: number) {
  if (!config.public.loqateKey) {
    console.error("[LocationBox] NUXT_PUBLIC_LOQATE_KEY not configured");
    const { $notify } = useNuxtApp();
    $notify({
      text: "Address lookup isn't configured. Type your address instead.",
      variant: "error",
    });
    locationLoading.value = false;
    return;
  }
  try {
    const data = await $fetch<{ Items: Record<string, unknown>[] }>(
      "https://api.addressy.com/Capture/Interactive/GeoLocation/v1.00/json3.ws",
      {
        params: {
          Key: config.public.loqateKey,
          Latitude: lat,
          Longitude: long,
          Items: 20,
          Radius: 50,
        },
      },
    );
    addresses.value = data?.Items || [];
    if (!addresses.value.length) {
      const { $notify } = useNuxtApp();
      $notify({
        text: "No addresses found near you. Type your address instead.",
        variant: "info",
      });
    }
  } catch (err) {
    console.error("[LocationBox] geo lookup failed", err);
    const { $notify } = useNuxtApp();
    $notify({
      text: "Address lookup failed. Type your address instead.",
      variant: "error",
    });
  } finally {
    locationLoading.value = false;
  }
}

function handleInput(e: Event) {
  addressString.value = (e.target as HTMLInputElement).value;
  if (queryTimer) clearTimeout(queryTimer);
  queryTimer = setTimeout(() => {
    const query = addressString.value.trim();
    // Don't spend a Loqate lookup on 1–2 chars (noise). No result-dedupe: it blocked
    // a retry after a failed lookup and left the dropdown empty when the field was
    // cleared and the same text retyped. Debounce + min-length already cut the volume.
    if (query.length < 3) {
      addresses.value = [];
      return;
    }
    lookupAddress(query);
  }, 400);
}

async function lookupAddress(query: string) {
  try {
    const data = await $fetch<{ Items: Record<string, unknown>[] }>(
      "https://api.addressy.com/Capture/Interactive/Find/v1.1/json3.ws",
      {
        params: {
          Key: config.public.loqateKey,
          Text: query,
          Countries: "GB",
          Limit: 10,
        },
      },
    );
    addresses.value = data?.Items || [];
  } catch {
    // lookup failed
  }
}

async function handleSelectAddress(address: Record<string, unknown>) {
  addressString.value = address.Text as string;

  if (address.Type !== "Address") {
    // Container — drill down
    locationLoading.value = true;
    try {
      const data = await $fetch<{ Items: Record<string, unknown>[] }>(
        "https://api.addressy.com/Capture/Interactive/Find/v1.1/json3.ws",
        { params: { Key: config.public.loqateKey, Container: address.Id as string } },
      );
      addresses.value = data?.Items || [];
    } catch {
      // drill-down failed
    }
    locationLoading.value = false;
    return;
  }

  // Final address — retrieve full structured fields
  try {
    const data = await $fetch<{ Items: Record<string, unknown>[] }>(
      "https://api.addressy.com/Capture/Interactive/Retrieve/v1.20/json3.ws",
      { params: { Key: config.public.loqateKey, Id: address.Id as string } },
    );
    const full = data?.Items?.[0];
    if (full) setLocation(full);
  } catch {
    // retrieve failed
  }
}

function setLocation(value: Record<string, unknown>) {
  const formatted = {
    line_1: (value.Line1 as string) || "",
    line_2: (value.Line2 as string) || "",
    city: (value.City as string) || "",
    zip: (value.PostalCode as string) || "",
  };
  emit("addressChanged", { formatted, raw: value });
  addresses.value = [];
}
</script>

<template>
  <div
    class="location-box"
    :style="{ width: props.width }"
  >
    <div class="location-box__search">
      <div
        class="location-box__search--location-button"
        @click="locatorButtonPressed"
      >
        <fa
          v-if="!locationLoading"
          :icon="['fal', 'location-arrow']"
        />
        <fa
          v-else
          :icon="['fal', 'spinner']"
          class="fa-spin"
        />
      </div>

      <input
        ref="searchTextField"
        class="location-box__search--input"
        :value="addressString"
        autocomplete="ignore"
        placeholder="Search for your address"
        @input="handleInput"
        @focus="clearInput"
      />
    </div>

    <div
      v-show="addresses.length"
      class="results-container"
    >
      <div class="results-container__results">
        <div
          v-for="address in addresses"
          :key="address.Id as string"
          class="results-container__results--option"
          @click="handleSelectAddress(address)"
        >
          <p>{{ displayAddress(address) }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.location-box {
  transition: 0.5s;
  width: 100%;
  z-index: 10;
  position: relative;
  margin: 0.6rem 0;

  &__search {
    // Single seamless border on the wrapper so the icon + input read as one
    // control matching the site's standard text input (main.scss). The inner
    // <input> resets the global input border (which out-specifies it).
    display: flex;
    align-items: stretch;
    border: 1px solid var(--color-grey-3);
    border-radius: var(--radius-sm);
    overflow: hidden;

    &:focus-within {
      border-color: var(--color-pink-1);
    }

    &--input {
      cursor: text;
      border: none !important;
      box-shadow: none !important;
      border-radius: 0;
      font-size: 0.9rem;
      font-family: inherit;
      width: 100%;

      @media (max-width: $tablet) {
        width: 90%;
      }

      &:focus {
        outline: none;
      }
    }

    &--location-button {
      background-color: var(--color-white-1);
      border: none;
      cursor: pointer;
      display: flex;
      align-items: center;
      padding: 0 10px 0 0;

      svg {
        color: var(--color-grey-2);
        margin-left: 10px;
      }

      @media (max-width: $tablet) {
        margin: 0 -2px 0 0;
        width: 30px;
      }
    }
  }
}

.results-container {
  &__results {
    box-sizing: border-box;
    position: absolute;
    left: 0;
    right: 0;
    border-radius: 0 0 0.4rem 0.4rem;
    z-index: 5555;
    background-color: var(--color-white-1);
    overflow-y: auto;
    border: 2px solid var(--color-grey-3);
    max-height: 15rem;

    &--option {
      box-sizing: border-box;
      width: 100%;
      padding: 5px 20px;
      border-top: 2px solid var(--color-grey-3);
      cursor: pointer;

      p {
        margin: 0.3rem 0;
      }
    }

    &--option:first-child {
      border-top: none;
    }
  }
}
</style>
